// # Email validation utility functions

// # Regular expression for basic email validation
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// # List of common disposable email domains
const DISPOSABLE_DOMAINS = [
  'tempmail.com',
  'temp-mail.org',
  'disposablemail.com',
  'mailinator.com',
  'guerrillamail.com',
  'sharklasers.com',
  'throwawaymail.com',
  '10minutemail.com',
  'yopmail.com',
  // Add more as needed
];

// # List of valid academic email domains (optional)
const ACADEMIC_DOMAINS = [
  'edu',
  'ac.uk',
  'edu.eg',
  // Add more academic domains as needed
];

/**
 * # Validates email format and checks against disposable email providers
 * @param {string} email - The email address to validate
 * @returns {{isValid: boolean, message: string}} Validation result and message
 */
export const validateEmail = (email) => {
  // # Check if email is empty
  if (!email) {
    return {
      isValid: false,
      message: 'Email is required'
    };
  }

  // # Check basic email format with a more permissive regex
  if (!EMAIL_REGEX.test(email)) {
    return {
      isValid: false,
      message: 'Please enter a valid email address'
    };
  }

  // # Get domain from email
  const domain = email.split('@')[1].toLowerCase();

  // # Check for disposable email domains
  if (DISPOSABLE_DOMAINS.some(disposableDomain => 
    domain.includes(disposableDomain.toLowerCase())
  )) {
    return {
      isValid: false,
      message: 'Disposable email addresses are not allowed'
    };
  }

  // # Optional: Check for academic email domains
  // const isAcademic = ACADEMIC_DOMAINS.some(academicDomain => 
  //   domain.endsWith(academicDomain.toLowerCase())
  // );
  
  return {
    isValid: true,
    message: 'Email is valid'
  };
};

/**
 * # Checks if the email domain has valid MX records
 * @param {string} email - The email address to validate
 * @returns {Promise<{isValid: boolean, message: string}>} Validation result and message
 */
export const validateEmailDomain = async (email) => {
  try {
    const domain = email.split('@')[1];
    
    // # Call backend API to check MX records
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/validate-email-domain`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ domain })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to validate email domain');
    }

    return {
      isValid: data.isValid,
      message: data.message
    };
  } catch (error) {
    console.error('Error validating email domain:', error);
    return {
      isValid: false,
      message: 'Unable to verify email domain. Please try again.'
    };
  }
};

// # Enhanced email validation that includes MX record check
export const validateEmailComplete = (email) => {
  return validateEmail(email);
};
